/**
 * Bot Configuration - per-bot AI config
 * Route: /bots/:botId/config
 * Guard: user must have access to bot via tenantBots
 */
import type { BotSystem } from '../lib/botNav.ts';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useBotRoute } from '../lib/useBotRoute';
import { botPath } from '../lib/botNav';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  hubService,
  type Bot,
  type DomainProfile,
  type Context,
  type CreateContextDto,
  type Persona,
  type CreatePersonaDto,
  type LlmCatalogModel,
  type BotRuntimeConfigResponse,
  type PromptTemplate,
  type CreatePromptTemplateDto,
  type Faq,
  type CreateFaqDto,
  type ReasoningStrategy,
  type CreateReasoningStrategyDto,
} from '../services/hub.service';
import { MessageBundlesTab } from '../components/aiconfig';
import { hubClient, type BotInstructions, type BotReadiness, type BotRouting, type BotRoutingChange } from '../services/hubClient';
import {
  INSTRUCTIONS_MAX_CHARS,
  activeStatus,
  canSwitchFlow,
  checkLabel,
  checkTone,
  errorText,
  fixSection,
  flowLabel,
  rollupText,
  routingRows,
  validateInstructions,
} from '../lib/assistantView';
import { useMe } from '../contexts/MeContext';
import {
  ACTION_LABEL,
  canRestore,
  restoreConfirm,
  sizeLabel,
  type InstructionVersionSummary,
} from '../lib/instructionsHistoryView';


type Tab =
  | 'ai'
  | 'bot'
  | 'context'
  | 'persona'
  | 'llm'
  | 'prompt-templates'
  | 'faq'
  | 'message-bundles'
  | 'rag-documents'
  | 'reasoning';

const TABS: { key: Tab; label: string }[] = [
  { key: 'ai', label: 'AI' },
  { key: 'bot', label: 'Bot' },
  { key: 'context', label: 'Context' },
  { key: 'persona', label: 'Persona' },
  { key: 'llm', label: 'LLM (advanced)' },
  { key: 'prompt-templates', label: 'Prompt Templates' },
  // { key: 'faq', label: 'FAQ' },
  { key: 'message-bundles', label: 'Message bundles' },
  { key: 'rag-documents', label: 'RAG Documents ↗' },
  { key: 'reasoning', label: 'Reasoning' },
];

export function BotConfig() {
  // const { tenantSlug, botId } = useParams<{ tenantSlug: string; botId: string }>();
  const { system, botId } = useBotRoute();
  const navigate = useNavigate();
  const { tenants, tenantBots } = useTenant();
  const [tab, setTab] = useState<Tab>('ai');

  const tenant = tenants?.find((t) => {
    const bots = tenantBots?.[t.id] ?? [];
    return bots.some((b) => b.botId === botId);
  });
  const bots = tenant ? tenantBots?.[tenant.id] ?? [] : [];
  const hasAccess = bots.some((b) => b.botId === botId);

  useEffect(() => {
    if (!tenant || !hasAccess) {
      navigate('/');
    }
  }, [tenant, hasAccess, navigate]);

  const tenantSlug = tenant?.slug;
  const bot = bots.find((b) => b.botId == botId);

  if (!tenant || !hasAccess || !botId) {
    return null;
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to={botPath({ system, botId }, 'cache')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Cache
          </Link>
          <Link
            to={botPath({ system, botId }, 'usage')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Usage
          </Link>
          <Link
            to="/cloud"
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Cloud (system)
          </Link>
          <Link
            to={botPath({ system, botId }, 'tools')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Tools
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Settings
          </h1>
        </div>
        {/* <div className="mb-6 space-y-1">
          <p className="text-sm text-gray-500">
            botId: {botId} · system: {system}
          </p>
          {pagesForBot.length > 0 ? (
            <p className="text-sm text-gray-600">
              Connected: {pagesForBot.map((p) => `${p.pageName || p.pageId} (${p.channel})`).join(', ')}
            </p>
          ) : (
            <p className="text-sm text-amber-600">No connected pages</p>
          )}
        </div> */}
        <p className="text-gray-600 mb-6">
          Configure intents, context, persona, models, tools, message bundles and more for
          this bot.
        </p>

        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                tab === t.key
                  ? 'bg-white border border-b-0 border-gray-200 -mb-px text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'bot' && <BotTab botId={bot?.id!} system={system} />}
        {tab === 'ai' && (
          // Keyed by bot: nothing loaded for — or still in flight from — the previous bot reaches this one.
          <AiTab key={`${system}/${botId}`} bot={{ system, botId }} />
        )}
        {tab === 'context' && <ContextTab botId={bot?.id!} system={system} />}
        {tab === 'persona' && <PersonaTab botId={bot?.id!} system={system} />}
        {tab === 'llm' && <LlmTab botId={botId} system={system} />}
        {tab === 'prompt-templates' && <PromptTemplatesTab botId={bot?.id!} system={system} />}
        {/* {tab === 'faq' && <FaqTab botId={botId} system={system} />} */}
        {tab === 'message-bundles' && <MessageBundlesTab botId={bot?.id!} system={system} />}
        {tab === 'rag-documents' && (
          // Documents moved to the bot's Knowledge page (A2 step A); the tab stays so old links land somewhere useful.
          <p className="text-sm text-gray-700">
            Documents are on the{' '}
            <Link to={botPath({ system, botId }, 'knowledge')} className="text-blue-600 hover:underline">
              Knowledge
            </Link>{' '}
            page now.
          </p>
        )}
        {tab === 'reasoning' && <ReasoningTab botId={bot?.id!} system={system} />}
      </div>
    </div>
  );
}

function BotTab({ botId, system }: { botId: string; system: BotSystem }) {
  const { getToken } = useAuth();
  const [bot, setBot] = useState<Bot | null>(null);
  const [domains, setDomains] = useState<DomainProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const notInHub = !loading && !error && bot === null;

  useEffect(() => {
    load();
  }, [botId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const [b, d] = await Promise.all([
        hubService.getBotOrNull(botId, system, token),
        hubService.listDomainProfiles(false, token),
      ]);
      setBot(b);
      setDomains(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    setCreating(true);
    setError(null);
    try {
      const token = getToken();
      await hubService.createBot(botId, system, botId, token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(selected: string[]) {
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const next = selected.length === 0 ? null : selected;
      await hubService.updateBotEnabledDomains(botId, next, system, token);
      setBot((prev) => (prev ? { ...prev, enabledDomains: next } : null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  if (notInHub) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          This bot is not yet registered in the Hub. Register it to configure enabled domains for coarse routing.
        </p>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
        >
          {creating ? 'Creating...' : 'Create Bot'}
        </button>
      </div>
    );
  }

  // bot exists
  const current = bot!.enabledDomains ?? [];
  const toggle = (id: string) => {
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    handleSave(next);
  };
  const setAll = () => handleSave(domains.map((d) => d.domain_id));
  const clearAll = () => handleSave([]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Enabled domains for coarse routing. Empty = all domains. Used by coarse-router.service.
      </p>
      <div className="flex gap-2 mb-4">
        <button
          onClick={setAll}
          className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded"
        >
          Select all
        </button>
        <button
          onClick={clearAll}
          className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded"
        >
          Clear (all domains)
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {domains.map((d) => (
          <label
            key={d.id}
            className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer ${
              current.includes(d.domain_id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
          >
            <input
              type="checkbox"
              checked={current.includes(d.domain_id)}
              onChange={() => toggle(d.domain_id)}
              disabled={saving}
            />
            <span className="text-sm">{d.domain_id}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ContextTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Context[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateContextDto>({
    system,
    botId,
    name: '',
    context: '',
  });

  useEffect(() => {
    load();
  }, [botId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listContextsByBot(botId, system, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.name.trim() || !form.context.trim()) return;
    try {
      const token = getToken();
      await hubService.createContext({ ...form, botId }, token);
      setCreating(false);
      setForm({ system, botId, name: '', context: '' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this context?')) return;
    try {
      const token = getToken();
      await hubService.deleteContext(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {!creating ? (
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          Add Context
        </button>
      ) : (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <input
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Context (JSON or text)"
            value={form.context}
            onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            rows={4}
          />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
              Save
            </button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start">
              <span className="font-medium">{item.name}</span>
              <button
                onClick={() => handleDelete(item.id)}
                className="text-red-600 hover:underline text-sm"
              >
                Delete
              </button>
            </div>
            <pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-32">{item.context}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

function PersonaTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreatePersonaDto>({
    system,
    botId,
    persona: '',
  });

  useEffect(() => {
    load();
  }, [botId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listPersonasByBot(botId, system, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.persona.trim()) return;
    try {
      const token = getToken();
      await hubService.createPersona({ ...form, botId }, token);
      setCreating(false);
      setForm({ system, botId, persona: '' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this persona?')) return;
    try {
      const token = getToken();
      await hubService.deletePersona(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {!creating ? (
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          Add Persona
        </button>
      ) : (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <textarea
            placeholder="Persona (JSON or text)"
            value={form.persona}
            onChange={(e) => setForm((f) => ({ ...f, persona: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            rows={4}
          />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
              Save
            </button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start">
              <span className="font-medium">{item.name || 'Persona'}</span>
              <button
                onClick={() => handleDelete(item.id)}
                className="text-red-600 hover:underline text-sm"
              >
                Delete
              </button>
            </div>
            <pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-32">{item.persona}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Which LLM this bot's answers come from.
 *
 * Replaces two screens that were both broken: an "LLM Models" tab whose API returned 404,
 * and a "Cloud" tab that failed to load because it fetched that same dead route. Both were
 * earlier attempts at per-bot model selection; neither had a working backend, and the table
 * behind the Cloud tab had no rows in production.
 *
 * The model list is a shared, priced catalog rather than per-bot rows, because price is a
 * property of (vendor, model) and must not be per-bot. Selecting is an ordinary PUT — no
 * confirmation, no progress bar — deliberately unlike the embedding profile next door,
 * where switching is a data migration. Making them look alike would imply this one is
 * expensive to undo, and it is not.
 */
function LlmTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [config, setConfig] = useState<BotRuntimeConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, [botId, system]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const cfg = await hubService.getBotRuntimeConfig(system, botId, token);
      setConfig(cfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function save(dto: Parameters<typeof hubService.updateBotRuntimeConfig>[2]) {
    setSaving(true);
    setError(null);
    try {
      setConfig(await hubService.updateBotRuntimeConfig(system, botId, dto, getToken()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;

  // A bot with no row is not an error: it has never been configured, which is the normal
  // state, and it means the deployment default. Rendering it as an empty form would suggest
  // something is missing when nothing is.
  const toolsEnabled = config?.llm?.toolsEnabled ?? false;
  const stream = config?.llm?.stream ?? false;

  return (
    <div className="space-y-6">
      {error && <div className="text-red-600 text-sm">{error}</div>}

      <p className="text-sm text-gray-500">The bot's model is chosen on the AI tab; these are advanced options.</p>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="text-sm font-medium text-gray-700">Capabilities</div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={toolsEnabled}
            disabled={saving}
            onChange={(e) => save({ toolsEnabled: e.target.checked })}
          />
          Tool calling
        </label>
        {/* Three independent gates must all hold, and an operator who only knows about this
            one will report the feature as broken. Say so here rather than in a wiki. */}
        <p className="text-xs text-gray-500 -mt-2 ml-6">
          Also requires flow version v2 on this bot, and the vendor being on tool-service's
          TOOLS_CAPABLE_VENDORS allowlist.
          {config?.llm && !config.llm.toolsCapable && (
            <span className="text-amber-600"> This model is not marked tool-capable.</span>
          )}
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={stream}
            disabled={saving}
            onChange={(e) => save({ llmStream: e.target.checked })}
          />
          Streaming
        </label>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Resolved</div>
        <dl className="text-sm text-gray-600 space-y-1">
          <div className="flex gap-2">
            <dt className="w-28 text-gray-500">Vendor</dt>
            <dd className="font-mono">{config?.llm?.vendor ?? 'vllm (deployment)'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-28 text-gray-500">Model</dt>
            <dd className="font-mono">{config?.llm?.model ?? '— deployment default —'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-28 text-gray-500">Flow version</dt>
            <dd className="font-mono">{config?.flowVersion ?? 'v1'}</dd>
          </div>
        </dl>
        {/* The pointer is an override, not a guarantee: tool-service falls back to the
            deployment default when a vendor's key is absent from its environment, which
            only tool-service can know. Promising the selection always wins would be a lie. */}
        <p className="mt-3 text-xs text-gray-500">
          If the selected vendor has no API key configured in tool-service, the bot keeps
          answering on the deployment default. Check the bot's readiness panel to see what it
          actually resolved to.
        </p>
      </div>
    </div>
  );
}

function PromptTemplatesTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const plannerTemplateDefault = `You are an AI Planner for a chatbot system.

Your job is to:
1. Understand user intent
2. Decide next actions using available tools
3. Extract structured parameters
4. Ask for missing required information
5. NEVER call tools directly (you only output a plan)
6. ALWAYS return valid JSON

Return ONLY a single JSON object. No explanation before or after.`;
  const [items, setItems] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreatePromptTemplateDto>({
    system,
    botId,
    name: '',
    category: 'dag',
    template: '',
  });

  useEffect(() => {
    load();
  }, [botId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listPromptTemplatesByBot(botId, system, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.name.trim() || !form.template.trim()) return;
    try {
      const token = getToken();
      await hubService.createPromptTemplate({ ...form, botId }, token);
      setCreating(false);
      setForm({ system, botId, name: '', category: 'dag', template: '' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this template?')) return;
    try {
      const token = getToken();
      await hubService.deletePromptTemplate(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {!creating ? (
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          Add Template
        </button>
      ) : (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  name: 'agent_planner_system',
                  category: 'agent_planner',
                  template: plannerTemplateDefault,
                }))
              }
              className="px-2.5 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-100"
            >
              Use Planner Default
            </button>
            <span className="text-xs text-gray-500 self-center">
              Prefills `agent_planner_system` template.
            </span>
          </div>
          <input
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            placeholder="Category (e.g. dag, router, intent_verification)"
            value={form.category ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Template ({{variable}} placeholders)"
            value={form.template}
            onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            rows={4}
          />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
              Save
            </button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start">
              <span className="font-medium">
                {item.name}
                {item.category ? (
                  <span className="ml-2 inline-block rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                    {item.category}
                  </span>
                ) : null}
              </span>
              <button
                onClick={() => handleDelete(item.id)}
                className="text-red-600 hover:underline text-sm"
              >
                Delete
              </button>
            </div>
            <pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-24">{item.template}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateFaqDto>({
    system,
    botId,
    question: '',
    answer: '',
  });

  useEffect(() => {
    load();
  }, [botId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listFaqsByBot(botId, system, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.question.trim() || !form.answer.trim()) return;
    try {
      const token = getToken();
      await hubService.createFaq({ ...form, botId }, token);
      setCreating(false);
      setForm({ system, botId, question: '', answer: '' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this FAQ?')) return;
    try {
      const token = getToken();
      await hubService.deleteFaq(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {!creating ? (
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          Add FAQ
        </button>
      ) : (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <input
            placeholder="Question"
            value={form.question}
            onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Answer"
            value={form.answer}
            onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            rows={3}
          />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
              Save
            </button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start">
              <span className="font-medium">{item.question}</span>
              <button
                onClick={() => handleDelete(String(item.id))}
                className="text-red-600 hover:underline text-sm"
              >
                Delete
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-600">{item.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReasoningTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<ReasoningStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [configTextError, setConfigTextError] = useState<string | null>(null);
  type StrategyType = 'standard' | 'cot' | 'tot' | 'react';
  type NodeScope =
    | '*'
    | 'RAG'
    | 'WORKFLOW_TOOL'
    | 'SERVICE'
    | 'SLOT'
    | 'CLARIFICATION'
    | 'FALLBACK'
    | 'INTENT';
  const NODE_OPTIONS: NodeScope[] = [
    '*',
    'RAG',
    'WORKFLOW_TOOL',
    'SERVICE',
    'SLOT',
    'CLARIFICATION',
    'FALLBACK',
    'INTENT',
  ];
  const STRATEGY_OPTIONS: StrategyType[] = ['standard', 'cot', 'tot', 'react'];
  const [form, setForm] = useState<CreateReasoningStrategyDto>({
    system,
    botId,
    strategyType: 'standard',
    config: {},
    enabled: true,
    priority: 0,
    name: '',
    description: '',
  });
  const [configText, setConfigText] = useState<string>(
    JSON.stringify(
      {
        appliesTo: ['*'],
      },
      null,
      2,
    ),
  );

  function normalizeStrategyType(v: string): StrategyType {
    return STRATEGY_OPTIONS.includes(v as StrategyType) ? (v as StrategyType) : 'standard';
  }

  function getDefaultConfig(type: StrategyType): Record<string, unknown> {
    if (type === 'cot') {
      return {
        appliesTo: ['RAG'],
        cot: { style: 'hidden', maxSteps: 4 },
        budget: { maxTokens: 700 },
      };
    }
    if (type === 'react') {
      return {
        appliesTo: ['WORKFLOW_TOOL', 'SERVICE'],
        react: { maxIterations: 3, toolCallEnabled: true, allowedTools: [] },
        budget: { maxTokens: 700 },
      };
    }
    if (type === 'tot') {
      return {
        appliesTo: ['FALLBACK'],
        tot: { branches: 2, depth: 1, selection: 'best' },
        budget: { maxTokens: 650 },
      };
    }
    return {
      appliesTo: ['*'],
    };
  }

  function updateConfig(updater: (prev: Record<string, unknown>) => Record<string, unknown>) {
    setForm((prev) => {
      const nextConfig = updater((prev.config as Record<string, unknown>) ?? {});
      setConfigText(JSON.stringify(nextConfig, null, 2));
      return { ...prev, config: nextConfig };
    });
  }

  function setStrategyType(type: StrategyType) {
    const next = getDefaultConfig(type);
    setForm((prev) => ({ ...prev, strategyType: type, config: next }));
    setConfigText(JSON.stringify(next, null, 2));
    setConfigTextError(null);
  }

  function toStringArray(v: unknown): string[] {
    return Array.isArray(v) ? v.map((x) => String(x)) : [];
  }

  const parsedConfig = (form.config as Record<string, unknown>) ?? {};
  const selectedNodes = toStringArray(parsedConfig.appliesTo);
  const budget = (parsedConfig.budget as Record<string, unknown>) ?? {};
  const cot = (parsedConfig.cot as Record<string, unknown>) ?? {};
  const react = (parsedConfig.react as Record<string, unknown>) ?? {};
  const tot = (parsedConfig.tot as Record<string, unknown>) ?? {};
  const reactAllowedTools =
    Array.isArray(react.allowedTools) && react.allowedTools.length
      ? react.allowedTools.map((x) => String(x)).join(', ')
      : '';

  useEffect(() => {
    load();
  }, [botId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listReasoningStrategiesByBot(botId, system, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const token = getToken();
      await hubService.createReasoningStrategy({ ...form, botId }, token);
      setCreating(false);
      const next = getDefaultConfig('standard');
      setForm({
        system,
        botId,
        strategyType: 'standard',
        config: next,
        enabled: true,
        priority: 0,
        name: '',
        description: '',
      });
      setConfigText(JSON.stringify(next, null, 2));
      setConfigTextError(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this strategy?')) return;
    try {
      const token = getToken();
      await hubService.deleteReasoningStrategy(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {!creating ? (
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          Add Strategy
        </button>
      ) : (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <input
            placeholder="Name (optional)"
            value={form.name ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <select
            value={form.strategyType}
            onChange={(e) => setStrategyType(normalizeStrategyType(e.target.value))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="standard">standard</option>
            <option value="cot">cot</option>
            <option value="tot">tot</option>
            <option value="react">react</option>
          </select>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-gray-600">Priority</span>
              <input
                type="number"
                min={0}
                value={form.priority ?? 0}
                onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))}
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={form.enabled ?? true}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              Enabled
            </label>
          </div>
          <textarea
            placeholder="Description (optional)"
            value={form.description ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            rows={2}
          />

          <div className="rounded border border-gray-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-gray-700 uppercase">Applies To Nodes</p>
            <div className="flex flex-wrap gap-2">
              {NODE_OPTIONS.map((node) => {
                const active = selectedNodes.includes(node);
                return (
                  <button
                    key={node}
                    type="button"
                    onClick={() =>
                      updateConfig((prev) => {
                        const current = toStringArray(prev.appliesTo);
                        const next = current.includes(node)
                          ? current.filter((x) => x !== node)
                          : [...current, node];
                        return { ...prev, appliesTo: next.length ? next : ['*'] };
                      })
                    }
                    className={`px-2 py-1 text-xs rounded border ${
                      active ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    {node}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded border border-gray-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-700 uppercase">Budget</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-gray-600">Max tokens</span>
                <input
                  type="number"
                  min={1}
                  value={Number(budget.maxTokens ?? 500)}
                  onChange={(e) =>
                    updateConfig((prev) => ({
                      ...prev,
                      budget: { ...(prev.budget as Record<string, unknown>), maxTokens: Number(e.target.value) || 500 },
                    }))
                  }
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-gray-600">Timeout ms</span>
                <input
                  type="number"
                  min={0}
                  value={Number(budget.timeoutMs ?? 0)}
                  onChange={(e) =>
                    updateConfig((prev) => ({
                      ...prev,
                      budget: { ...(prev.budget as Record<string, unknown>), timeoutMs: Number(e.target.value) || 0 },
                    }))
                  }
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-gray-600">Max cost USD</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={Number(budget.maxCostUsd ?? 0)}
                  onChange={(e) =>
                    updateConfig((prev) => ({
                      ...prev,
                      budget: { ...(prev.budget as Record<string, unknown>), maxCostUsd: Number(e.target.value) || 0 },
                    }))
                  }
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
            </div>
          </div>

          {form.strategyType === 'cot' && (
            <div className="rounded border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700 uppercase">CoT Settings</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600">Max steps</span>
                  <input
                    type="number"
                    min={1}
                    value={Number(cot.maxSteps ?? 4)}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        cot: { ...(prev.cot as Record<string, unknown>), maxSteps: Number(e.target.value) || 4 },
                      }))
                    }
                    className="rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600">Style</span>
                  <select
                    value={String(cot.style ?? 'hidden')}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        cot: { ...(prev.cot as Record<string, unknown>), style: e.target.value },
                      }))
                    }
                    className="rounded border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="hidden">hidden</option>
                    <option value="brief">brief</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {form.strategyType === 'react' && (
            <div className="rounded border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700 uppercase">ReAct Settings</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600">Max iterations</span>
                  <input
                    type="number"
                    min={1}
                    value={Number(react.maxIterations ?? 3)}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        react: {
                          ...(prev.react as Record<string, unknown>),
                          maxIterations: Number(e.target.value) || 3,
                        },
                      }))
                    }
                    className="rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input
                    type="checkbox"
                    checked={Boolean(react.toolCallEnabled ?? true)}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        react: { ...(prev.react as Record<string, unknown>), toolCallEnabled: e.target.checked },
                      }))
                    }
                  />
                  Tool call enabled
                </label>
              </div>
              <textarea
                placeholder="Allowed tools (comma separated), e.g. search_docs,create_order"
                value={reactAllowedTools}
                onChange={(e) =>
                  updateConfig((prev) => ({
                    ...prev,
                    react: {
                      ...(prev.react as Record<string, unknown>),
                      allowedTools: e.target.value
                        .split(',')
                        .map((x) => x.trim())
                        .filter(Boolean),
                    },
                  }))
                }
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                rows={2}
              />
            </div>
          )}

          {form.strategyType === 'tot' && (
            <div className="rounded border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700 uppercase">ToT Settings</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600">Branches</span>
                  <input
                    type="number"
                    min={2}
                    value={Number(tot.branches ?? 2)}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        tot: { ...(prev.tot as Record<string, unknown>), branches: Number(e.target.value) || 2 },
                      }))
                    }
                    className="rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600">Depth</span>
                  <input
                    type="number"
                    min={1}
                    value={Number(tot.depth ?? 1)}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        tot: { ...(prev.tot as Record<string, unknown>), depth: Number(e.target.value) || 1 },
                      }))
                    }
                    className="rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600">Selection</span>
                  <select
                    value={String(tot.selection ?? 'best')}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        tot: { ...(prev.tot as Record<string, unknown>), selection: e.target.value },
                      }))
                    }
                    className="rounded border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="best">best</option>
                    <option value="majority">majority</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          <textarea
            placeholder="Config (JSON)"
            value={configText}
            onChange={(e) => {
              try {
                const c = JSON.parse(e.target.value || '{}');
                setConfigText(e.target.value);
                setConfigTextError(null);
                setForm((f) => ({ ...f, config: c }));
              } catch {
                setConfigText(e.target.value);
                setConfigTextError('Invalid JSON');
              }
            }}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
            rows={8}
          />
          {configTextError && <p className="text-xs text-red-600">{configTextError}</p>}
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={Boolean(configTextError)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => {
                setCreating(false);
                setConfigTextError(null);
              }}
              className="px-3 py-1.5 text-sm bg-gray-200 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg border">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {items.map((item) => (
            <tr key={item.id}>
              <td className="px-4 py-2 text-sm">{item.name || '-'}</td>
              <td className="px-4 py-2 text-sm font-mono">{item.strategyType}</td>
              <td className="px-4 py-2 text-xs font-mono text-gray-600">
                {Array.isArray((item.config as { appliesTo?: unknown })?.appliesTo)
                  ? ((item.config as { appliesTo?: unknown[] }).appliesTo ?? []).join(', ')
                  : '*'}
              </td>
              <td className="px-4 py-2 text-sm">{item.priority ?? 0}</td>
              <td className="px-4 py-2 text-sm">{item.enabled === false ? 'No' : 'Yes'}</td>
              <td className="px-4 py-2 text-right">
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:underline text-sm"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── A2 step 1: readiness + instructions (chatbot_v2_mono spec 2026-10-06-ai-console-v2-first-design.md §A2) ──

const TONE_CLS: Record<'ok' | 'bad' | 'unknown', string> = {
  ok: 'bg-green-50 text-green-700 border-green-200',
  bad: 'bg-red-50 text-red-700 border-red-200',
  unknown: 'bg-amber-50 text-amber-700 border-amber-200',
};
const TONE_MARK = { ok: '✓', bad: '✗', unknown: '?' } as const;

/** Whether the bot answers with the LLM, and what to fix when it does not. */
function ReadinessCard({ bot, refreshKey }: { bot: { system: BotSystem; botId: string }; refreshKey: string }) {
  const [r, setR] = useState<BotReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setError(null);
    hubClient
      .getBotReadiness(bot)
      .then((x) => !stale && setR(x))
      .catch((e) => !stale && setError(errorText(e)));
    return () => {
      stale = true;
    };
  }, [bot.system, bot.botId, refreshKey]);

  const roll = r ? (r.ready === 'ready' ? 'ok' : r.ready === 'not_ready' ? 'bad' : 'unknown') : 'unknown';
  return (
    <section className={`mb-6 rounded-lg border p-4 ${TONE_CLS[roll]}`} aria-label="Readiness">
      <div className="font-semibold">{error ? `Readiness unavailable: ${error}` : r ? rollupText(r.ready) : 'Checking readiness…'}</div>
      {r && !error && (
        <ul className="mt-3 space-y-1 text-sm">
          {r.checks.map((c) => {
            const tone = checkTone(c.status);
            const fix = tone !== 'ok' ? fixSection(c.key) : null;
            return (
              <li key={c.key} className="flex items-start gap-2">
                <span aria-hidden className="w-4 font-bold">{TONE_MARK[tone]}</span>
                <span className="font-medium">{checkLabel(c.key)}</span>
                <span className="text-gray-700">— {c.detail}</span>
                {fix && (
                  <button
                    type="button"
                    aria-label={`Fix ${checkLabel(c.key)}`}
                    onClick={() => {
                      const el = document.getElementById(fix);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      el?.focus({ preventScroll: true });
                    }}
                    className="ml-auto shrink-0 underline"
                  >
                    Fix
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** The bot's instructions — the text tool-service puts in front of every LLM turn. */
function InstructionsTab({ bot, onSaved }: { bot: { system: BotSystem; botId: string }; onSaved: () => void }) {
  const [loaded, setLoaded] = useState<BotInstructions | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const bumpHistory = () => setHistoryKey((n) => n + 1);

  useEffect(() => {
    let stale = false;
    setLoaded(null);
    setError(null);
    hubClient
      .getBotInstructions(bot)
      .then((x) => {
        if (stale) return;
        setLoaded(x);
        setText(x.content);
      })
      .catch((e) => !stale && setError(errorText(e)));
    return () => {
      stale = true;
    };
  }, [bot.system, bot.botId]);

  /**
   * After a failed write, re-read what is actually stored: tool-service replaces by delete-then-add, so a failed
   * save may already have removed the old text. The operator's draft stays in the editor.
   */
  function recheck() {
    hubClient
      .getBotInstructions(bot)
      .then((x) => setLoaded(x))
      .catch(() => undefined)
      .finally(() => {
        bumpHistory();
        onSaved();
      });
  }

  async function save() {
    const invalid = validateInstructions(text);
    if (invalid) return setError(invalid);
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const x = await hubClient.putBotInstructions(bot, text);
      setLoaded(x);
      setText(x.content);
      setNotice('Saved.');
      bumpHistory();
      onSaved();
    } catch (e) {
      setError(errorText(e));
      recheck();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Remove this bot\'s instructions? The LLM will stop answering for it.')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await hubClient.deleteBotInstructions(bot);
      setLoaded({ ...(loaded as BotInstructions), configured: false, content: '' });
      setText('');
      setNotice('Removed.');
      bumpHistory();
      onSaved();
    } catch (e) {
      setError(errorText(e));
      recheck();
    } finally {
      setBusy(false);
    }
  }

  // Unreachable is never shown as "no instructions": the editor stays closed until a real answer arrives.
  if (!loaded) return <div className={error ? 'text-red-600 text-sm' : 'text-gray-500'}>{error ?? 'Loading...'}</div>;

  const dirty = text.trim() !== loaded.content.trim();
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Persona and rules the LLM follows for this bot ({bot.system}). Without them the bot replies “Ботын заавар
        тохируулаагүй байна.”
        {loaded.active === false && ' The bot is currently inactive (see Active below).'}
      </p>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      {notice && <div className="text-green-700 text-sm">{notice}</div>}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={16}
        aria-label="Instructions"
        className="w-full rounded border border-gray-300 p-3 font-mono text-sm"
        placeholder={loaded.configured ? '' : 'No instructions yet.'}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {loaded.configured && (
          <button type="button" onClick={remove} disabled={busy} className="rounded border border-red-300 px-4 py-2 text-sm text-red-700">
            Remove
          </button>
        )}
        <span className="ml-auto text-xs text-gray-500">
          {text.trim().length} / {INSTRUCTIONS_MAX_CHARS}
        </span>
      </div>
      <InstructionsHistory
        bot={bot}
        refreshKey={historyKey}
        draftDirty={dirty}
        onRestored={(x) => {
          setLoaded(x);
          setText(x.content);
          setError(null);
          setNotice('Restored.');
          bumpHistory();
          onSaved();
        }}
      />
    </div>
  );
}

/**
 * Every change to the instructions, newest first (the hub keeps copies; tool-service keeps the text). View loads a
 * version's text on demand; Restore saves it again as a new version, so restoring never erases anything.
 */
function InstructionsHistory({
  bot,
  refreshKey,
  draftDirty,
  onRestored,
}: {
  bot: { system: BotSystem; botId: string };
  refreshKey: number;
  draftDirty: boolean;
  onRestored: (x: BotInstructions) => void;
}) {
  const [rows, setRows] = useState<InstructionVersionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [texts, setTexts] = useState<Record<string, string | null>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setError(null);
    hubClient
      .listInstructionVersions(bot)
      .then((x) => !stale && setRows(x.versions))
      .catch((e) => !stale && setError(errorText(e)));
    return () => {
      stale = true;
    };
  }, [bot.system, bot.botId, refreshKey]);

  // chatbot_front's v1 screen links here with #ai-instructions-history.
  useEffect(() => {
    if (rows && window.location.hash === '#ai-instructions-history') {
      document.getElementById('ai-instructions-history')?.scrollIntoView();
    }
  }, [rows !== null]);

  async function view(id: string) {
    if (open === id) return setOpen(null);
    setOpen(id);
    if (id in texts) return;
    try {
      const v = await hubClient.getInstructionVersion(bot, id);
      setTexts((t) => ({ ...t, [id]: v.content }));
    } catch (e) {
      setError(errorText(e));
    }
  }

  async function restore(id: string) {
    if (!window.confirm(restoreConfirm(draftDirty))) return;
    setBusy(id);
    setError(null);
    try {
      onRestored(await hubClient.restoreInstructionVersion(bot, id));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div id="ai-instructions-history" tabIndex={-1} className="border-t border-gray-200 pt-3">
      <h3 className="mb-2 text-sm font-semibold text-gray-800">History</h3>
      {error && <div className="mb-2 text-sm text-red-600">{error}</div>}
      {rows === null && !error && <div className="text-sm text-gray-500">Loading...</div>}
      {rows?.length === 0 && (
        <div className="text-sm text-gray-500">No changes recorded yet. Every change from now on is kept here.</div>
      )}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-gray-100 text-sm">
          {rows.map((v) => (
            <li key={v.id} className="py-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-gray-500">{new Date(v.createdAt).toLocaleString()}</span>
                <span className="font-medium">{ACTION_LABEL[v.action]}</span>
                <span className="text-gray-600">{v.actor ?? '—'}</span>
                <span className="text-xs text-gray-500">{sizeLabel(v)}</span>
                <span className="ml-auto flex gap-2">
                  {canRestore(v) && (
                    <button type="button" onClick={() => view(v.id)} className="text-blue-700 underline">
                      {open === v.id ? 'Hide' : 'View'}
                    </button>
                  )}
                  {canRestore(v) && (
                    <button
                      type="button"
                      onClick={() => restore(v.id)}
                      disabled={busy !== null}
                      className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-50"
                    >
                      {busy === v.id ? 'Restoring…' : 'Restore'}
                    </button>
                  )}
                </span>
              </div>
              {open === v.id && (
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 font-mono text-xs">
                  {v.id in texts ? (texts[v.id] ?? '') : 'Loading...'}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Everything that decides whether the bot answers, on one tab — as v1 /hub/botconfig, and for the same reason:
 * with config and instructions on separate screens an operator filled one, never found the other, and every turn
 * answered "unregistered". Storage stays split (hub `bot_runtime_config` = control plane; tool-service
 * `static_vectors` = instructions); the UI does not. Readiness is re-read after every save here.
 */
function AiTab({ bot }: { bot: { system: BotSystem; botId: string } }) {
  const [saves, setSaves] = useState(0);
  const bump = () => setSaves((n) => n + 1);
  return (
    <div className="space-y-6">
      <ReadinessCard bot={bot} refreshKey={String(saves)} />
      <section id="ai-instructions" tabIndex={-1} className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="mb-2 text-sm font-semibold text-gray-800">Instructions</h2>
        <InstructionsTab bot={bot} onSaved={bump} />
      </section>
      <RoutingSection bot={bot} onSaved={bump} />
      <RuntimeSection bot={bot} onSaved={bump} />
    </div>
  );
}

/** Flow version, active switch, LLM model and embedding — the bot's hub control-plane row. */
function RuntimeSection({ bot, onSaved }: { bot: { system: BotSystem; botId: string }; onSaved: () => void }) {
  const { getToken } = useAuth();
  const me = useMe();
  const [catalog, setCatalog] = useState<LlmCatalogModel[]>([]);
  const [config, setConfig] = useState<BotRuntimeConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let stale = false;
    const token = getToken();
    // Settled, not all-or-nothing: a catalog failure must not hide the bot's config, and a config failure must
    // never render as "no hub row" (that state drives the Turn on / Turn off buttons).
    Promise.allSettled([hubService.listLlmCatalog(token), hubService.getBotRuntimeConfig(bot.system, bot.botId, token)])
      .then(([models, cfg]) => {
        if (stale) return;
        if (models.status === 'fulfilled') setCatalog(models.value);
        if (cfg.status === 'fulfilled') {
          setConfig(cfg.value);
          setLoadFailed(false);
        } else {
          setLoadFailed(true);
          setError(errorText(cfg.reason));
        }
      })
      .finally(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
  }, [bot.system, bot.botId]);

  async function save(dto: Parameters<typeof hubService.updateBotRuntimeConfig>[2]) {
    setSaving(true);
    setError(null);
    try {
      setConfig(await hubService.updateBotRuntimeConfig(bot.system, bot.botId, dto, getToken()));
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  function switchFlow(next: string) {
    if (next === 'v2' && !window.confirm('Switch this bot to flow v2? It is experimental (tool use, in development) and changes how every reply is built.')) {
      return;
    }
    void save({ flowVersion: next });
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (loadFailed) return <div className="text-red-600 text-sm">Could not load the bot's settings: {error}</div>;

  const flow = config?.flowVersion ?? 'v1';
  // No hub row: tool-service follows the legacy Qdrant payload flag (or says "not configured"). Readiness above
  // shows what that resolves to; the first save creates the row.
  const active = activeStatus(config);
  const selected = config?.llm?.modelId ?? '';
  const selectedUnavailable = !!selected && catalog.find((m) => m.id === selected)?.available === 'unavailable';
  // Only when EVERY row is unknown: one unknown row among available ones is noise, but a
  // wholly unknown list means the check itself did not happen and the picker is unfiltered.
  const catalogUnknown = catalog.length > 0 && catalog.every((m) => m.available === 'unknown');

  return (
    <div className="space-y-6">
      {error && <div className="text-red-600 text-sm">{error}</div>}

      <section id="ai-active" tabIndex={-1} className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div>
          {active === 'unset' ? (
            <div>
              <div className="text-sm font-medium text-gray-800">Active</div>
              <p className="mt-1 text-xs text-gray-500">
                Not set in the hub yet — serving follows the bot's older stored flag (see readiness above).
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save({ isActive: true })}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  Turn on
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save({ isActive: false })}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
                >
                  Turn off
                </button>
              </div>
            </div>
          ) : (
            <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={active === 'on'}
                disabled={saving}
                onChange={(e) => void save({ isActive: e.target.checked })}
              />
              Active
            </label>
          )}
          <p className="mt-1 ml-6 text-xs text-gray-500">When off, the bot keeps its instructions but the LLM does not answer.</p>
        </div>
        <div>
          <div className="text-sm font-medium text-gray-800">Flow version</div>
          {canSwitchFlow(me) ? (
            <select
              aria-label="Flow version"
              className="mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
              value={flow}
              disabled={saving}
              onChange={(e) => switchFlow(e.target.value)}
            >
              <option value="v1">{flowLabel('v1')}</option>
              <option value="v2">{flowLabel('v2')}</option>
            </select>
          ) : (
            <div className="mt-1 font-mono text-sm text-gray-700">{flowLabel(flow)}</div>
          )}
          <p className="mt-1 text-xs text-gray-500">v2 is in development in tool-service; only platform admins switch it.</p>
        </div>
      </section>

      <ModelPicker
        catalog={catalog}
        selected={selected}
        saving={saving}
        selectedUnavailable={selectedUnavailable}
        catalogUnknown={catalogUnknown}
        onChange={(id) => void save({ llmModelId: id || null })}
      />

      <section id="ai-embedding" tabIndex={-1} className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-medium text-gray-800">Embedding</div>
        <div className="mt-1 font-mono text-sm text-gray-700">{config?.embeddingProfile ?? 'bge_m3'}</div>
        <p className="mt-1 text-xs text-gray-500">
          All bots use bge-m3. Another embedder needs its own embedder service and Qdrant collections — deferred.
        </p>
      </section>
    </div>
  );
}

function ModelPicker({
  catalog,
  selected,
  saving,
  selectedUnavailable,
  catalogUnknown,
  onChange,
}: {
  catalog: LlmCatalogModel[];
  selected: string;
  saving: boolean;
  selectedUnavailable: boolean;
  catalogUnknown: boolean;
  onChange: (modelId: string) => void;
}) {
  return (
      <div id="ai-model" tabIndex={-1} className="bg-white rounded-lg border border-gray-200 p-4">
        <label htmlFor="ai-model-select" className="block text-sm font-medium text-gray-700 mb-1">LLM model</label>
        <select
          id="ai-model-select"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          value={selected}
          disabled={saving}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Deployment default (self-hosted vLLM)</option>
          {catalog.map((m) => (
            <option key={m.id} value={m.id} disabled={m.available === 'unavailable'}>
              {m.label} — {m.vendor}/{m.model}
              {m.available === 'unavailable' ? ' (unavailable)' : ''}
            </option>
          ))}
        </select>
        {selectedUnavailable && (
          // Worth saying out loud: an unusable model is not an error anywhere. tool-service
          // falls back to the deployment default and the bot keeps answering, so without
          // this line the only symptom is that the chosen provider quietly never runs.
          <p className="mt-2 text-xs text-amber-700">
            This model is currently unavailable, so the bot is running on the deployment
            default. Its API key is missing in tool-service — an operator can fix that on the
            LLM Providers page.
          </p>
        )}
        {catalogUnknown && (
          <p className="mt-2 text-xs text-gray-500">
            Availability could not be checked (tool-service unreachable), so nothing is greyed
            out. A model whose key is missing falls back to the deployment default.
          </p>
        )}
        <p className="mt-2 text-xs text-gray-500">
          Leave on the deployment default unless this bot needs a specific provider. Changing
          this takes effect within a minute and is reversible at any time — nothing is
          migrated and no history is lost.
        </p>
      </div>
  );
}

/**
 * Bot routing R1 (spec chatbot_v2_mono 2026-10-09-bot-routing-design.md): which engines answer the bot, per surface /
 * channel — the hub record that v1 chatbot_mn, the v1 routing page and the v2 channel switch to. Editable by global
 * admins / services (same rule as flow); read-only otherwise. Each change is one validated, logged PUT.
 */
function RoutingSection({ bot, onSaved }: { bot: { system: BotSystem; botId: string }; onSaved: () => void }) {
  const me = useMe();
  const [routing, setRouting] = useState<BotRouting | null>(null);
  const [history, setHistory] = useState<BotRoutingChange[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const [r, h] = await Promise.all([hubClient.getBotRouting(bot), hubClient.getBotRoutingHistory(bot, 5)]);
      setRouting(r);
      setHistory(h);
    } catch (e) {
      setError(errorText(e));
    }
  }

  useEffect(() => {
    void reload();
  }, [bot.system, bot.botId]);

  async function change(field: string, value: string) {
    setSaving(field);
    setError(null);
    try {
      setRouting(await hubClient.changeBotRouting(bot, field, value));
      setHistory(await hubClient.getBotRoutingHistory(bot, 5).catch(() => history));
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(null);
    }
  }

  const editable = canSwitchFlow(me);
  return (
    <section id="ai-routing" tabIndex={-1} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
      <h2 className="text-sm font-semibold text-gray-800">Channels</h2>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      {!routing ? (
        !error && <div className="text-gray-500 text-sm">Loading...</div>
      ) : (
        <>
          <table className="text-sm">
            <tbody>
              {routingRows(bot.system, routing.flow).map((row) => (
                <tr key={row.field}>
                  <td className="py-1 pr-6 text-gray-700">{row.label}</td>
                  <td className="py-1">
                    {editable ? (
                      <select
                        aria-label={row.label}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        value={row.value}
                        disabled={saving !== null}
                        onChange={(e) => void change(row.field, e.target.value)}
                      >
                        {row.options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="font-mono">{row.value}</span>
                    )}
                    {row.field === 'block_links' && (
                      <Link to={`${botPath(bot, 'knowledge')}?tab=blocks`} className="ml-3 text-xs text-blue-600 hover:underline">
                        Blocks and bundle text
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!routing.stored && <p className="text-xs text-gray-500">Not set in the hub yet — the defaults are shown.</p>}
          {history.length > 0 && (
            <div className="text-xs text-gray-500">
              <div className="font-medium text-gray-600">Recent changes</div>
              <ul>
                {history.map((h) => (
                  <li key={h.id}>
                    {new Date(h.createdAt).toLocaleString()} · {h.actor ?? 'unknown'} ·{' '}
                    {Object.entries(h.changes)
                      .map(([f, [from, to]]) => `${f}: ${from} → ${to}`)
                      .join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

